import { HttpError } from '@/lib/https';
import type { AnalyticsEnvelope } from '../types/analytics-api.types';

const RANGE_ERROR = "'from' must not be after 'to'.";

export class AnalyticsRequestError extends Error {
  status?: number;

  constructor(message: string, status?: number) {
    super(message);
    this.name = 'AnalyticsRequestError';
    this.status = status;
  }
}

/** Split `"<Field>: <message>"` on the first colon-space. Bare 404/409/410 messages stay whole. */
export function parseAnalyticsErrorEntry(entry: string): { field: string | null; message: string } {
  const splitAt = entry.indexOf(': ');
  if (splitAt <= 0) return { field: null, message: entry };
  return { field: entry.slice(0, splitAt), message: entry.slice(splitAt + 2) };
}

export function analyticsErrorMessages(
  response: AnalyticsEnvelope<unknown> | null | undefined
): string[] {
  const errors = Array.isArray(response?.errors) ? response.errors.filter(Boolean) : [];
  if (errors.length) return errors.map((entry) => parseAnalyticsErrorEntry(entry).message);
  if (typeof response?.message === 'string' && response.message.trim()) {
    return [response.message.trim()];
  }
  return [];
}

export function assertAnalyticsRange(from?: string, to?: string): void {
  if (from && to && from > to) {
    throw new AnalyticsRequestError(RANGE_ERROR, 400);
  }
}

export function unwrapAnalytics<T>(
  response: AnalyticsEnvelope<T> | null | undefined,
  fallback: string
): T {
  if (!response || typeof response !== 'object') {
    throw new AnalyticsRequestError(fallback);
  }
  if (response.success === false) {
    const messages = analyticsErrorMessages(response);
    throw new AnalyticsRequestError(messages[0] || fallback);
  }
  if (response.data === undefined || response.data === null) {
    throw new AnalyticsRequestError(`${fallback}: missing data payload`);
  }
  return response.data;
}

export function getAnalyticsErrorMessage(error: unknown): string {
  if (error instanceof AnalyticsRequestError) return error.message;
  if (error instanceof HttpError) {
    if (error.status === 401) return 'Sign in again to view reports.';
    if (error.status === 403) return 'You don’t have permission to view this report.';
    const messages = analyticsErrorMessages(error.error as AnalyticsEnvelope<unknown>);
    if (messages[0]) return messages[0];
    return error.message || 'Report request failed.';
  }
  if (error instanceof Error && error.message) return error.message;
  return 'Report request failed.';
}

export function analyticsErrorStatus(error: unknown): number | undefined {
  if (error instanceof AnalyticsRequestError) return error.status;
  if (error instanceof HttpError) return error.status;
  return undefined;
}
